import { useMutation, useQueryClient } from "@tanstack/react-query";
import { startAuthentication } from "@simplewebauthn/browser";
import { apiFetch, ApiError } from "../api/client";
import { ME_KEY } from "../api/hooks/useMe";
import type { Me } from "../api/types";

function onSession(qc: ReturnType<typeof useQueryClient>) {
  return (me: Me) => {
    qc.setQueryData(ME_KEY, me);
    qc.invalidateQueries({ queryKey: ["workspaces"] });
  };
}

// Second-factor step: exchange the mfaToken + a TOTP or recovery code for a
// session.
export function useTotpSignIn() {
  const qc = useQueryClient();
  return useMutation<Me, ApiError, { mfaToken: string; code: string }>({
    mutationFn: async (input) => {
      const res = await apiFetch<{ user: Me }>("/auth/sign-in/totp", {
        method: "POST",
        body: input,
      });
      return res.user;
    },
    onSuccess: onSession(qc),
  });
}

// Standalone passwordless sign-in: fetch options, run the browser WebAuthn
// ceremony, verify. The thrown error may be a DOMException (user cancelled /
// no authenticator) rather than an ApiError, so the mutation error is Error.
export function usePasskeySignIn() {
  const qc = useQueryClient();
  return useMutation<Me, Error, { email?: string }>({
    mutationFn: async ({ email }) => {
      const optionsJSON = await apiFetch<Parameters<typeof startAuthentication>[0]["optionsJSON"]>(
        "/auth/passkey/authenticate/options",
        { method: "POST", body: { email: email || undefined } },
      );
      const response = await startAuthentication({ optionsJSON });
      const res = await apiFetch<{ user: Me }>("/auth/passkey/authenticate/verify", {
        method: "POST",
        body: { response },
      });
      return res.user;
    },
    onSuccess: onSession(qc),
  });
}
